
import { supabase } from '@/integrations/supabase/client';
import { trackContentView, trackContentImport } from './analyticsService';

// Define content types for stats tracking
export type ContentType = 'game_plan' | 'technique';

/**
 * Increment the view count for shared content
 * @param contentType The type of content ('game_plan' or 'technique')
 * @param shareId The unique share ID of the content
 * @returns A promise that resolves to the new view count or null if an error occurred
 */
export async function incrementViewCount(contentType: ContentType, shareId: string): Promise<number | null> {
  if (!shareId) {
    console.warn(`Cannot increment view count: No share ID provided`);
    return null;
  }
  
  try {
    console.log(`Incrementing view count for ${contentType} with ID:`, shareId);
    
    // Call the unified increment_view_count database function
    const { data, error } = await supabase.rpc('increment_view_count', {
      content_type: contentType,
      share_id: shareId
    });
    
    if (error) {
      console.error(`Error incrementing view count:`, error);
      return null;
    }
    
    // Track the view using the analytics system
    trackContentView(contentType, shareId);
    
    return data || null;
  } catch (error) {
    console.error(`Error in incrementViewCount:`, error);
    return null;
  }
}

/**
 * Increment the import count for shared content
 * @param contentType The type of content ('game_plan' or 'technique')
 * @param shareId The unique share ID of the content
 * @returns A promise that resolves to the new import count or null if an error occurred
 */
export async function incrementImportCount(contentType: ContentType, shareId: string): Promise<number | null> {
  if (!shareId) {
    console.warn(`Cannot increment import count: No share ID provided`);
    return null;
  }
  
  try {
    console.log(`Incrementing import count for ${contentType} with ID:`, shareId);
    
    // Call the unified increment_import_count database function
    const { data, error } = await supabase.rpc('increment_import_count', {
      content_type: contentType,
      share_id: shareId
    });
    
    if (error) {
      console.error(`Error incrementing import count:`, error);
      return null;
    }
    
    // Track the import using the analytics system
    trackContentImport(contentType, shareId);
    
    return data || null;
  } catch (error) {
    console.error(`Error in incrementImportCount:`, error);
    return null;
  }
}

/**
 * Record a referral conversion
 * @param referralCode The referral code to record a conversion for
 * @param contentType The type of content involved in the conversion
 * @param shareId The unique share ID of the content
 * @param source The source of the conversion
 */
export async function recordReferralConversion(
  referralCode: string,
  contentType: ContentType,
  shareId: string,
  source: string = 'content_import'
): Promise<void> {
  if (!referralCode || !shareId) return;
  
  try {
    console.log("Recording referral conversion for code:", referralCode);
    
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.warn("No authenticated user for referral conversion");
      return;
    }
    
    // Get the referrer from the referral code
    const { data: referralCodeData } = await supabase
      .from('referral_codes')
      .select('user_id')
      .eq('code', referralCode)
      .single();
    
    if (referralCodeData) {
      // Insert the referral conversion
      await supabase
        .from('referrals')
        .insert({
          referrer_id: referralCodeData.user_id,
          referred_user_id: user.id,
          source: source,
          status: 'converted'
        });
    }
    
    // Track the conversion using the analytics system
    trackContentImport(contentType, shareId, { source, referralCode });
  } catch (error) {
    console.error("Error in recordReferralConversion:", error);
    // Non-critical operation, don't throw
  }
}
